import { get, set } from 'idb-keyval';

/**
 * v5 - Paramètres de personnalisation de l'écran de confirmation de commande
 */

export type ConfirmationTemplate =
  | 'classic'      // Orange dégradé (actuel)
  | 'elegant'      // Sombre + or
  | 'nature'       // Vert/beige
  | 'ocean'        // Bleu turquoise
  | 'minimal'      // Blanc/noir épuré
  | 'custom';      // Tout personnalisé

export type NumberStyle =
  | 'plain'        // Texte simple blanc
  | 'gradient'     // Dégradé
  | 'glow'         // Halo lumineux
  | 'neon'         // Effet néon
  | 'outline';     // Contour seulement

export type BadgeShape = 'pill' | 'square' | 'badge';
export type CornerRadius = 'none' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'full';
export type FxType = 'none' | 'confetti' | 'particles' | 'glow' | 'sparkle';
export type AnimationType = 'fade' | 'scale' | 'slide-up' | 'zoom-bounce';
export type FontFamily = 'default' | 'serif' | 'mono' | 'display';

export interface ConfirmationDesignSettings {
  // Template de base
  template: ConfirmationTemplate;
  // Couleurs
  bgColor1: string;          // Couleur de fond gradient début
  bgColor2: string;          // Couleur de fond gradient fin
  textColor: string;         // Couleur principale du texte
  numberColor: string;       // Couleur du numéro de commande
  accentColor: string;       // Couleur d'accent (badges, icônes)
  // Image de fond
  backgroundImage: string | null;  // base64 ou null
  backgroundOpacity: number;       // 0 - 100
  backgroundBlur: number;          // 0 - 20
  // Formes
  popupRadius: CornerRadius;
  badgeShape: BadgeShape;
  // Typographie
  numberStyle: NumberStyle;
  numberSize: number;        // 4xl - 9xl (en valeur 4-9)
  fontFamily: FontFamily;
  // Animations & effets
  enterAnimation: AnimationType;
  fxType: FxType;
  // v5: Animation Lottie (checkout)
  showLottieAnimation: boolean;    // afficher l'animation checkout
  lottieLoop: boolean;             // joue une fois (false, recommandé) ou en boucle (true)
  lottieSize: 'small' | 'medium' | 'large';
  hasCustomLottie: boolean;        // true si l'admin a uploadé son propre Lottie
  // Affichage
  showOrderType: boolean;
  showCustomerName: boolean;
  showOrderDetails: boolean;
  showPriceBreakdown: boolean;
  // Texte custom
  congratsMessage: string;   // Message de félicitations en haut (optionnel)
}

const STORAGE_KEY = 'confirmationDesignSettings';

export const DEFAULT_DESIGN: ConfirmationDesignSettings = {
  template: 'classic',
  bgColor1: '#f97316', // orange-500
  bgColor2: '#dc2626', // red-600
  textColor: '#ffffff',
  numberColor: '#fef3c7', // yellow-100
  accentColor: '#f97316',
  backgroundImage: null,
  backgroundOpacity: 30,
  backgroundBlur: 0,
  popupRadius: '2xl',
  badgeShape: 'pill',
  numberStyle: 'gradient',
  numberSize: 6,
  fontFamily: 'default',
  enterAnimation: 'scale',
  fxType: 'particles', // v5: particules subtiles par défaut (pro)
  // v5: Animation Lottie — désactivée par défaut (anti-lag), à activer dans l'admin
  showLottieAnimation: false,
  lottieLoop: false,        // joue 1 fois puis se fige (économise CPU)
  lottieSize: 'medium',
  hasCustomLottie: false,   // utilise le Lottie par défaut sauf si admin upload le sien
  showOrderType: true,
  showCustomerName: true,
  showOrderDetails: true,
  showPriceBreakdown: true,
  congratsMessage: '',
};

/**
 * Templates pré-faits
 */
export const TEMPLATES: Record<
  Exclude<ConfirmationTemplate, 'custom'>,
  Partial<ConfirmationDesignSettings>
> = {
  classic: {
    template: 'classic',
    bgColor1: '#f97316',
    bgColor2: '#dc2626',
    textColor: '#ffffff',
    numberColor: '#fef3c7',
    accentColor: '#f97316',
    numberStyle: 'gradient',
    fxType: 'particles', // v5: particules par défaut
  },
  elegant: {
    template: 'elegant',
    bgColor1: '#1f2937',  // gray-800
    bgColor2: '#111827',  // gray-900
    textColor: '#ffffff',
    numberColor: '#fbbf24', // amber-400 (or)
    accentColor: '#fbbf24',
    numberStyle: 'glow',
    fxType: 'particles',
    popupRadius: 'xl',
    badgeShape: 'square',
    fontFamily: 'serif',
  },
  nature: {
    template: 'nature',
    bgColor1: '#16a34a',  // green-600
    bgColor2: '#84cc16',  // lime-500
    textColor: '#ffffff',
    numberColor: '#fefce8',  // yellow-50
    accentColor: '#16a34a',
    numberStyle: 'plain',
    fxType: 'glow',
    popupRadius: '2xl',
    fontFamily: 'default',
  },
  ocean: {
    template: 'ocean',
    bgColor1: '#0891b2',  // cyan-600
    bgColor2: '#2563eb',  // blue-600
    textColor: '#ffffff',
    numberColor: '#cffafe',  // cyan-100
    accentColor: '#0891b2',
    numberStyle: 'gradient',
    fxType: 'particles',
    popupRadius: '2xl',
  },
  minimal: {
    template: 'minimal',
    bgColor1: '#ffffff',
    bgColor2: '#f3f4f6',  // gray-100
    textColor: '#111827',  // gray-900
    numberColor: '#000000',
    accentColor: '#111827',
    numberStyle: 'plain',
    fxType: 'none',
    popupRadius: 'lg',
    badgeShape: 'pill',
  },
};

/**
 * Applique un template aux paramètres
 */
export function applyTemplate(
  current: ConfirmationDesignSettings,
  template: Exclude<ConfirmationTemplate, 'custom'>
): ConfirmationDesignSettings {
  return {
    ...current,
    ...TEMPLATES[template],
    template,
  };
}

/**
 * Charge les paramètres depuis IndexedDB
 */
export async function getConfirmationDesign(): Promise<ConfirmationDesignSettings> {
  try {
    const stored = await get<ConfirmationDesignSettings>(STORAGE_KEY);
    if (stored && typeof stored === 'object') {
      // Fusion avec les defaults pour les nouveaux champs
      return { ...DEFAULT_DESIGN, ...stored };
    }
  } catch (e) {
    console.error('[ConfirmationDesign] load error', e);
  }
  return DEFAULT_DESIGN;
}

/**
 * Sauvegarde les paramètres dans IndexedDB
 */
export async function saveConfirmationDesign(
  settings: ConfirmationDesignSettings
): Promise<boolean> {
  try {
    await set(STORAGE_KEY, settings);
    // Notifier le reste de l'app
    window.dispatchEvent(
      new CustomEvent('confirmationDesignUpdated', { detail: settings })
    );
    return true;
  } catch (e) {
    console.error('[ConfirmationDesign] save error', e);
    return false;
  }
}

/* ===========================================================================
 * v5 — Gestion du Lottie custom (séparé du design pour éviter de gonfler le state)
 * Le JSON est stocké dans IndexedDB sous la clé `customLottieAnimation`.
 * =========================================================================== */

const CUSTOM_LOTTIE_KEY = 'customLottieAnimation';
const MAX_LOTTIE_SIZE = 2 * 1024 * 1024; // 2 MB — largement suffisant pour un Lottie

/**
 * Vérifie qu'un objet est un JSON Lottie valide (structure minimale).
 */
export function isValidLottieJson(obj: any): boolean {
  if (!obj || typeof obj !== 'object') return false;
  // Un Lottie a TOUJOURS ces champs : v (version), fr (framerate), ip (in point),
  // op (out point), w (width), h (height), layers (array)
  return (
    typeof obj.v === 'string' &&
    typeof obj.fr === 'number' &&
    typeof obj.ip === 'number' &&
    typeof obj.op === 'number' &&
    typeof obj.w === 'number' &&
    typeof obj.h === 'number' &&
    Array.isArray(obj.layers)
  );
}

/**
 * Sauvegarde un Lottie custom dans IndexedDB.
 */
export async function saveCustomLottie(animationData: object): Promise<boolean> {
  try {
    await set(CUSTOM_LOTTIE_KEY, animationData);
    // Notifier pour rafraîchir le cache mémoire dans LottieCheckout
    window.dispatchEvent(new CustomEvent('customLottieUpdated'));
    return true;
  } catch (e) {
    console.error('[ConfirmationDesign] saveCustomLottie error', e);
    return false;
  }
}

/**
 * Récupère le Lottie custom depuis IndexedDB.
 */
export async function getCustomLottie(): Promise<object | null> {
  try {
    const stored = await get<object>(CUSTOM_LOTTIE_KEY);
    return stored || null;
  } catch (e) {
    console.error('[ConfirmationDesign] getCustomLottie error', e);
    return null;
  }
}

/**
 * Supprime le Lottie custom — revient au Lottie par défaut.
 */
export async function deleteCustomLottie(): Promise<boolean> {
  try {
    const { del } = await import('idb-keyval');
    await del(CUSTOM_LOTTIE_KEY);
    window.dispatchEvent(new CustomEvent('customLottieUpdated'));
    return true;
  } catch (e) {
    console.error('[ConfirmationDesign] deleteCustomLottie error', e);
    return false;
  }
}

export const LOTTIE_MAX_SIZE_BYTES = MAX_LOTTIE_SIZE;

/**
 * Helper pour construire le style CSS du fond
 */
export function buildBackgroundStyle(settings: ConfirmationDesignSettings): {
  base: React.CSSProperties;
  overlay?: React.CSSProperties;
} {
  const base: React.CSSProperties = {
    background: `linear-gradient(135deg, ${settings.bgColor1} 0%, ${settings.bgColor2} 100%)`,
  };

  if (settings.backgroundImage) {
    return {
      base,
      overlay: {
        backgroundImage: `url(${settings.backgroundImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        opacity: settings.backgroundOpacity / 100,
        filter: settings.backgroundBlur > 0 ? `blur(${settings.backgroundBlur}px)` : undefined,
      },
    };
  }
  return { base };
}

/**
 * Classes Tailwind pour les radius
 */
export const RADIUS_CLASSES: Record<CornerRadius, string> = {
  none: 'rounded-none',
  sm: 'rounded',
  md: 'rounded-md',
  lg: 'rounded-lg',
  xl: 'rounded-xl',
  '2xl': 'rounded-2xl',
  full: 'rounded-3xl',
};

/**
 * Classes pour la forme du badge
 */
export const BADGE_SHAPE_CLASSES: Record<BadgeShape, string> = {
  pill: 'rounded-full',
  square: 'rounded-lg',
  badge: 'rounded-md',
};

/**
 * Classes pour la taille du numéro
 */
export function getNumberSizeClass(size: number): string {
  const map: Record<number, string> = {
    4: 'text-4xl',
    5: 'text-5xl',
    6: 'text-6xl',
    7: 'text-7xl',
    8: 'text-8xl',
    9: 'text-9xl',
  };
  return map[size] || 'text-6xl';
}

/**
 * Classes pour la police
 */
export const FONT_FAMILY_CLASSES: Record<FontFamily, string> = {
  default: '',
  serif: 'font-serif',
  mono: 'font-mono',
  display: 'font-bold tracking-tight',
};

/**
 * Classe d'animation d'entrée
 */
export const ENTER_ANIMATION_CLASSES: Record<AnimationType, string> = {
  fade: 'confirmation-anim-fade',
  scale: 'confirmation-anim-scale',
  'slide-up': 'confirmation-anim-slide-up',
  'zoom-bounce': 'confirmation-anim-zoom-bounce',
};
